export type RatingShape = 'star' | 'heart' | 'emoji';

export interface RatingConfig {
  count: number;
  shape: RatingShape;
  lowLabel: string;
  highLabel: string;
  /** Move to the next step as soon as a rating is picked. */
  autoAdvance: boolean;
}

export interface OpinionScaleConfig {
  start: number;
  end: number;
  lowLabel: string;
  midLabel: string;
  highLabel: string;
  /** Red / amber / green buttons, as on a classic NPS question. */
  colorCoded: boolean;
  autoAdvance: boolean;
}

export interface SliderConfig {
  min: number;
  max: number;
  step: number;
  defaultValue: number;
  prefix: string;
  suffix: string;
  lowLabel: string;
  highLabel: string;
}

export const RATING_SHAPES: { value: RatingShape; label: string }[] = [
  { value: 'star', label: 'Stars' },
  { value: 'heart', label: 'Hearts' },
  { value: 'emoji', label: 'Emoji' },
];

export const RATING_EMOJIS = ['😞', '🙁', '😐', '🙂', '😍'];

export const RATING_LIMITS = { min: 3, max: 10 };
export const SCALE_END_LIMITS = { min: 2, max: 10 };

export const NPS_PRESET: Partial<OpinionScaleConfig> = {
  start: 0,
  end: 10,
  lowLabel: 'Not at all likely',
  midLabel: '',
  highLabel: 'Extremely likely',
  colorCoded: true,
};

export const createDefaultRatingConfig = (): RatingConfig => ({
  count: 5,
  shape: 'star',
  lowLabel: '',
  highLabel: '',
  autoAdvance: false,
});

export const createDefaultOpinionScaleConfig = (): OpinionScaleConfig => ({
  start: 1,
  end: 5,
  lowLabel: 'Disagree',
  midLabel: '',
  highLabel: 'Agree',
  colorCoded: false,
  autoAdvance: false,
});

export const createDefaultSliderConfig = (): SliderConfig => ({
  min: 0,
  max: 100,
  step: 1,
  defaultValue: 50,
  prefix: '',
  suffix: '',
  lowLabel: '',
  highLabel: '',
});

export const resolveRatingConfig = (value?: Partial<RatingConfig>): RatingConfig => ({
  ...createDefaultRatingConfig(),
  ...(value || {}),
});

export const resolveOpinionScaleConfig = (
  value?: Partial<OpinionScaleConfig>,
): OpinionScaleConfig => ({ ...createDefaultOpinionScaleConfig(), ...(value || {}) });

export const resolveSliderConfig = (value?: Partial<SliderConfig>): SliderConfig => ({
  ...createDefaultSliderConfig(),
  ...(value || {}),
});

export const SCALE_BLOCK_DEFAULTS: Record<string, () => object> = {
  RatingInput: createDefaultRatingConfig,
  OpinionScale: createDefaultOpinionScaleConfig,
  SliderInput: createDefaultSliderConfig,
};

export const SCALE_BLOCK_KEYS = Object.keys(SCALE_BLOCK_DEFAULTS);

export const emojiForPoint = (index: number, count: number): string => {
  if (count <= 1) return RATING_EMOJIS[RATING_EMOJIS.length - 1];
  const position = Math.round((index * (RATING_EMOJIS.length - 1)) / (count - 1));
  return RATING_EMOJIS[position];
};

export const npsTone = (value: number, start: number, end: number): 'low' | 'mid' | 'high' => {
  const ratio = (value - start) / Math.max(1, end - start);
  if (ratio <= 0.6) return 'low';
  if (ratio <= 0.8) return 'mid';
  return 'high';
};
